function getAbsolutePosition(position) {
  const {
    left,
    right,
    bottom,
    top,
    width,
    ...rest
  } = position;

  // adjust the coordinates so that it fits within the window
  const finalPosition = {
    left: Math.max(0, left),
    right: Math.max(0, right),
    top: Math.max(0, top),
    bottom // since it's ok the make the document grow downwards - no need to normalize here
  };
  const getAbsoluteDiff = (value1, value2) => Math.abs(Math.abs(value1) - Math.abs(value2));

  // adjust the width by whatever has been subtracted from left or right
  finalPosition.width = width - Math.abs(getAbsoluteDiff(left, finalPosition.left) - getAbsoluteDiff(right, finalPosition.right));
  if (finalPosition.top !== top) {
    finalPosition.bottom += finalPosition.top - top;
  }
  return {
    ...finalPosition,
    ...rest,
    isNormalized: false
  };
}
function invertPosition(position) {
  switch (position) {
    case 'above':
      return 'below';
    case 'below':
      return 'above';
    case 'left':
      return 'right';
    default:
      return 'left';
  }
}
function findRectByPosition(position, rectsArray) {
  return rectsArray.find(_ref => {
    let {
      position: rectPosition
    } = _ref;
    return rectPosition === position;
  });
}

/**
 * Used for absolute positioning of the tooltip. Since the tooltip is always centered relatively
 * to the control (button) and we have a limited set of positions, an easy and consistent way
 * of positioning it is to check all of the possible positions and pick one that will be rendered
 * within the window bounds. This way we can also rely on the tooltip being actually rendered
 * before it is shown, which makes it account for the width being limiting in styles, custom font
 * rendering, etc.
 */
function getTooltipPosition(position, _ref2) {
  let {
    controlLayout,
    tooltipDimensions,
    windowDimensions,
    arrowWidth = 0,
    arrowOffset = 0
  } = _ref2;
  const {
    width: controlWidth,
    height: controlHeight,
    x: controlX,
    y: controlY
  } = controlLayout;
  const {
    width: tooltipWidth,
    height: tooltipHeight
  } = tooltipDimensions;
  const {
    width: windowWidth,
    height: windowHeight
  } = windowDimensions;
  const arrowSize = arrowWidth / 2 + arrowOffset;
  const horizontalBounds = {
    left: controlX + controlWidth / 2 - tooltipWidth / 2,
    right: windowWidth - (controlX + controlWidth / 2 + tooltipWidth / 2)
  };
  const verticalBounds = {
    top: controlY,
    bottom: windowHeight - (controlY + controlHeight / 2 + tooltipHeight / 2)
  };

  // calculate absolute coordinates for each of the potential positions (relative to window)
  const boundingRects = [{
    position: 'above',
    ...horizontalBounds,
    top: controlY - tooltipHeight - arrowSize,
    bottom: windowHeight - (controlY - arrowSize)
  }, {
    position: 'right',
    ...verticalBounds,
    left: controlX + controlWidth + arrowSize,
    right: windowWidth - (controlX + controlWidth + tooltipWidth + arrowSize)
  }, {
    position: 'below',
    ...horizontalBounds,
    top: controlY + controlHeight + arrowSize,
    bottom: windowHeight - (controlY + controlHeight + tooltipHeight + arrowSize)
  }, {
    position: 'left',
    ...verticalBounds,
    left: controlX - tooltipWidth - arrowSize,
    right: windowWidth - (controlX - arrowSize)
  }].map(rect => {
    // an absolute value representing how much of the tooltip is overflowing the window on each side
    const windowOverflow = Math.abs(Math.min(rect.top, 0) - Math.min(rect.left, 0) - Math.min(rect.right, 0) - Math.min(rect.bottom, 0));
    return {
      ...rect,
      ...tooltipDimensions,
      overflow: windowOverflow,
      isNormalized: false
    };
  });

  // the 'position' prop overrides the automatic positioning
  if (position !== 'auto') {
    let rect = findRectByPosition(position, boundingRects);

    // check if the suggested 'position' fits in window
    if (rect.overflow === 0) {
      return rect;
    }

    // otherwise try the inverted position (e.g. left -> right)
    rect = findRectByPosition(invertPosition(position), boundingRects);
    if (rect.overflow === 0) {
      return rect;
    }
  }
  const inWindow = boundingRects.filter(_ref3 => {
    let {
      overflow
    } = _ref3;
    return overflow === 0;
  });

  // pick the first position that fits in window
  // (these are sorted clockwise which makes them show where one would expect them to be)
  if (inWindow.length > 0) {
    return inWindow[0];
  }

  // if all positions would end up being out of window bounds, let's pick the one that is
  // the least overflowing and normalize its position to fit within window bounds
  boundingRects.sort((_ref4, _ref5) => {
    let {
      overflow: overflowA
    } = _ref4;
    let {
      overflow: overflowB
    } = _ref5;
    return overflowA - overflowB;
  });
  const leastOverflowing = boundingRects[0];

  // prefer 'below' over 'above', since we can always expand the document downwards,
  // and 'above' might cause issues on small viewports with large tooltips
  return getAbsolutePosition(leastOverflowing.position === 'above' ? findRectByPosition('below', boundingRects) : leastOverflowing);
}
export default getTooltipPosition;